import { ref, computed, watch } from '@vue/composition-api'
import analytics from '@/@core/utils/analytics'
import {
  apiToastError,
  apiToastFailure,
  apiToastSuccess,
  apiToastWarning,
} from '@/@core/utils/toast'
import SampleSheet from '@/assets/sample/SampleProductSheet.xlsx'
import { dataURLtoFile, toBase64 } from '@/common-utils'
import {
  uploadMultipart,
  createUploadControl,
  findResumableUpload,
  abandonUpload,
  UploadCancelled,
} from '@/utils/multipartUploader'
import constants, {
  BRAND_CATALOG_ADDON_FILE_TYPE,
  BRAND_CATALOG_PDF_FILE_TYPE,
  FLAG_CONSTANTS,
  IMAGE_COMPRESSER,
  MONTH_START_DATE,
  UPLOAD_BRAND_CATALOG_ADDON_FILE_TYPES,
  UPLOAD_BRAND_CATALOG_FILE_TYPES,
  UPLOAD_BRAND_CATALOG_MAX_FILES,
  UPLOAD_CATALOG_FILE_TYPES,
  UPLOAD_COLLECTION_LOGO_FILE_TYPES,
} from '@/constants'
import {
  CREATE_COLLECTION,
  GET_COLLECTION_BY_ID,
  UPDATE_COLLECTION,
} from '@/store/modules/collection.module'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import {
  download,
  formatNumber,
  formatObject,
  parseErrorObject,
  textTruncate,
} from '@core/utils/utils'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import store from '@/store'
import Compress from 'compress.js'
import { Settings } from '@/Settings'
import DromoUploadCatalogHelperV1 from '@/dromo/DromoUploadCatalogHelperV1'
import DromoUploader from 'dromo-uploader-js'
import DromoUploadCatalogHelperV2 from '@/dromo/DromoUploadCatalogHelperV2'

const {
  dateStringToTime,
  formattedDate,
  getMonthAndYearFromDate,
} = utils

const {
  COLLECTION_TYPE,
  FILE_TYPES,
  KP_DATE_FORMAT,
  KP_MONTH_FORMAT,
} = c

export function useCollectionEdit(collectionIdParam) {
  // Form state
  const collectionId = ref(collectionIdParam || null)
  const collection = ref(buildDefault())
  const logoFile = ref(null)
  const logoPreview = ref(null)
  const productFile = ref(null)
  const dromoId = ref(null)
  const isDromoUpload = ref(false)
  const isFileUpload = ref(false)
  const isDromoUploadDone = ref(false)
  const fromDate = ref(null)
  const toDate = ref(null)

  // Loading state
  const uploading = ref(false)
  const loading = ref(false)
  const uploadPercentage = ref(0)
  const uploadMessage = ref(null)
  const uploadTime = ref(30)
  const ordersToUpdate = ref([])
  const payload = ref({})
  const isCropping = ref(false)
  const img = ref(null)
  const croppedImg = ref(null)

  // Computed
  const profile = computed(() => store.getters.profile)
  const isBrand = computed(() => store.getters.isBrand)
  const isEdit = computed(() => !!collectionId.value)
  const buttonText = computed(() => (isEdit.value ? 'Save Changes' : 'Add new collection'))
  const title = computed(() => (isEdit.value
    ? `Edit collection "${collection.value.name}"`
    : 'Add collection'))
  const isDromoRefactorEnabled = computed(
    () => store.state.flags?.[FLAG_CONSTANTS.DOWNLOAD_COLLECTION_WITH_ERRORS] ?? false,
  )
  const disabled = computed(() => (
    loading.value
    || uploading.value
    || !collection.value.type
    || !hasDates.value
  ))
  const hasDates = computed(() => (
    (fromDate.value && toDate.value)
    || (fromDate.value === null && toDate.value === null)
  ))

  const enableDromoDemo = Settings.getConfig('IS_DROMO_ENABLED') === 'true'

  // Validation (for V2 views with manual validation)
  const errors = ref({})

  function validateName() {
    if (!collection.value.name) {
      return 'Collection Name is required'
    }
    return null
  }

  function validateDescription() {
    if (!collection.value.description) {
      return 'Collection Description is required'
    }
    if (collection.value.description.length < 3) {
      return 'Collection Description must be at least 3 characters'
    }
    return null
  }

  const fieldValidators = {
    name: validateName,
    description: validateDescription,
  }

  function validateField(field) {
    const validator = fieldValidators[field]
    if (!validator) return true

    const error = validator()
    errors.value[field] = error
    return !error
  }

  function validateAll() {
    const nameValid = validateField('name')
    const descValid = validateField('description')
    return nameValid && descValid
  }

  // Methods
  function buildDefault() {
    return {
      name: '',
      description: '',
      endDate: '',
      startDate: '',
      startDateString: formattedDate(new Date()),
      endDateString: '',
      logo: '',
      productsFile: '',
      type: '',
      brandCatalogFiles: [],
      brandCatalogAddonFiles: [],
    }
  }

  function uploadTimeHandler() {
    if (uploadTime.value > 0) {
      setTimeout(() => {
        uploadTime.value -= 1
        if (loading.value || uploading.value) {
          uploadTimeHandler()
        }
      }, 1000)
    }
  }

  function resetForm() {
    collection.value = buildDefault()
  }

  function trackCollectionError(err) {
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_ENDS_ADDING_OR_EDITING_COLLECTION,
      formatObject({
        collectionStatus: 'Error',
        error: parseErrorObject(err),
      }),
    )
  }

  async function uploadLogo(progressCallback) {
    uploading.value = true
    if (logoFile.value instanceof File) {
      const loaderObj = {
        onUploadProgress: progressEvent => {
          uploadPercentage.value = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 20),
          )
          uploadMessage.value = `Uploading your Cover Photo... ${uploadPercentage.value}%`
          if (progressCallback) {
            progressCallback({
              uploadPercentage: uploadPercentage.value,
              uploadMessage: uploadMessage.value,
              showProgress: true,
            })
          }
        },
      }
      collection.value.logo = await store.dispatch(UPLOAD_DOCUMENTS, {
        file: logoFile.value,
        loaderObj,
        fileType: FILE_TYPES.COLLECTION_LOGO,
      })
    }
    uploading.value = false
  }

  async function uploadProduct(progressCallback) {
    uploading.value = true
    if (productFile.value instanceof File) {
      const allowedFileTypes = ['xlsx', 'zip', '7z']
      const fileType = productFile.value.name.split('.').pop().toLowerCase()
      if (!allowedFileTypes.includes(fileType)) {
        apiToastWarning('Invalid file type. Please upload a valid file.')
        uploading.value = false
        return false
      }
      const loaderObj = {
        onUploadProgress: progressEvent => {
          uploadPercentage.value = 20
            + parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 50),
            )
          if (uploadTime.value !== 0) {
            uploadMessage.value = `Uploading your File... ${uploadPercentage.value}%`
          } else {
            uploadMessage.value = 'This might take few seconds... Please wait'
          }
          if (progressCallback) {
            progressCallback({
              uploadPercentage: uploadPercentage.value,
              uploadMessage: uploadMessage.value,
              showProgress: true,
            })
          }
        },
      }
      collection.value.productsFile = await store.dispatch(
        UPLOAD_DOCUMENTS,
        {
          file: productFile.value,
          loaderObj,
          fileType: FILE_TYPES.COLLECTION_PRODUCTS,
        },
      )
    }
    uploading.value = false
    return true
  }

  function resetLoading(progressCallback) {
    loading.value = false
    if (progressCallback) {
      progressCallback({ showProgress: false })
    }
    uploadPercentage.value = 0
  }

  function buildDateFields(startDateString, endDateString) {
    const dates = {}
    if (startDateString) {
      dates.startDate = new Date(dateStringToTime(startDateString))
    }
    if (endDateString) {
      dates.endDate = new Date(dateStringToTime(endDateString))
    }
    return dates
  }

  function buildExpectedDeliveryWindow() {
    if (!fromDate.value || !toDate.value) return {}
    const fromDateString = `${MONTH_START_DATE}${fromDate.value}`
    const toDateString = `${MONTH_START_DATE}${toDate.value}`
    return {
      fromDate: new Date(dateStringToTime(fromDateString)),
      toDate: new Date(dateStringToTime(toDateString)),
    }
  }

  function applyFileOverrides(p) {
    if (!logoFile.value) delete p.logo
    if (dromoId.value) {
      p.dromoId = dromoId.value
      delete p.productsFile
    } else if (!productFile.value) {
      delete p.productsFile
    }
  }

  const keepFileFields = file => ({
    url: file.url,
    name: file.name,
    ...(file.size ? { size: file.size } : {}),
  })

  function buildPayload() {
    const {
      name,
      description,
      endDateString,
      startDateString,
      logo,
      type,
      productsFile,
      brandCatalogFiles,
      brandCatalogAddonFiles,
    } = collection.value
    const p = {
      name,
      description,
      logo,
      productsFile,
      type,
      // Keep `size`: the download popup totals it and the 2GB client-zip
      // tier is chosen from it. Dropping it meant any full-form save — a
      // rename, say — silently stripped the sizes the upload had just
      // recorded.
      brandCatalogFiles: (brandCatalogFiles || []).map(keepFileFields),
      brandCatalogAddonFiles: (brandCatalogAddonFiles || []).map(keepFileFields),
      // The first iteration's single-file fields are cleared on save — their
      // values now live in the arrays (applyCollectionData folds them in).
      brandCatalogFile: '',
      brandCatalogFileName: '',
      brandCatalogAddonFile: '',
      brandCatalogAddonFileName: '',
      ...buildDateFields(startDateString, endDateString),
      expectedDeliveryWindow: buildExpectedDeliveryWindow(),
    }
    applyFileOverrides(p)
    return p
  }

  function doNotUpdateOrders() {
    payload.value = {
      ...payload.value,
      shouldUpdateProductPrices: false,
    }
  }

  function updateOrdersPayload(orderIds) {
    payload.value = {
      ...payload.value,
      shouldUpdateProductPrices: true,
      orderIds,
    }
  }

  function buildSaveProgressLoader(progressCallback) {
    return {
      onUploadProgress: progressEvent => {
        uploadPercentage.value = 70
          + parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 30),
          )
        uploadMessage.value = `Saving Your Products... ${uploadPercentage.value}%`
        if (progressCallback) {
          progressCallback({
            uploadPercentage: uploadPercentage.value,
            uploadMessage: uploadMessage.value,
            showProgress: true,
          })
        }
      },
    }
  }

  function navigateAfterSave(router, callbacks) {
    if (router) router.push({ name: callbacks.redirectRouteName || 'collections' })
  }

  function trackCollectionSuccess(extraData = {}) {
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_ENDS_ADDING_OR_EDITING_COLLECTION,
      formatObject({ collectionStatus: 'Success', ...extraData }),
    )
  }

  async function createCollection(progressCallback, router, callbacks = {}) {
    const loaderObj = buildSaveProgressLoader(progressCallback)
    store
      .dispatch(CREATE_COLLECTION, { payload: payload.value, loaderObj })
      .then(res => {
        trackCollectionSuccess(payload.value)
        analytics.track(
          constants.TRACKS.ACTIONS.BRAND_COLLECTION_FILE_PROCEEDED,
          formatObject(res),
        )
        apiToastSuccess(res.data.message)
        resetLoading(progressCallback)
        navigateAfterSave(router, callbacks)
      })
      .catch(err => {
        resetLoading(progressCallback)
        trackCollectionError(err)
        analytics.track(
          constants.TRACKS.ACTIONS.BRAND_COLLECTION_FILE_PROCEEDED,
          formatObject(parseErrorObject(err)),
        )
      })
  }

  function isOrderPriceConflict(err) {
    return (
      productFile.value instanceof File
      && err.response.status === 441
      && err.response.data.data.shouldUpdateProductPrices
    )
  }

  function handleOrderPriceConflict(err, callbacks) {
    if (err?.response?.data?.data?.orders.length) {
      ordersToUpdate.value = err.response.data.data.orders
    }
    if (callbacks.onOrderConflict) callbacks.onOrderConflict()
  }

  function handleUpdateSuccess(res, progressCallback, router, callbacks) {
    if (callbacks.onSuccess) callbacks.onSuccess()
    resetLoading(progressCallback)
    apiToastSuccess(res.data.message)
    trackCollectionSuccess()
    navigateAfterSave(router, callbacks)
  }

  function handleUpdateError(err, progressCallback, callbacks) {
    if (isOrderPriceConflict(err)) {
      handleOrderPriceConflict(err, callbacks)
    } else {
      trackCollectionError(err)
    }
    resetLoading(progressCallback)
  }

  async function updateCollection(progressCallback, router, callbacks = {}) {
    const loaderObj = buildSaveProgressLoader(progressCallback)
    await store
      .dispatch(UPDATE_COLLECTION, {
        collectionId: collectionId.value,
        payload: payload.value,
        loaderObj,
      })
      .then(res => handleUpdateSuccess(res, progressCallback, router, callbacks))
      .catch(err => handleUpdateError(err, progressCallback, callbacks))
  }

  async function syncCollection(progressCallback, router, callbacks = {}) {
    if (brandCatalogUploading.value) {
      apiToastWarning('Please wait for the brand catalogue upload to finish.')
      return
    }
    loading.value = true
    uploadTimeHandler()

    if (progressCallback) {
      progressCallback({ showProgress: true })
    }

    await uploadLogo(progressCallback)
    const shouldProceed = await uploadProduct(progressCallback)
    if (!shouldProceed) {
      resetLoading(progressCallback)
      return
    }

    payload.value = buildPayload()

    try {
      if (collectionId.value) {
        await updateCollection(progressCallback, router, callbacks)
      } else {
        await createCollection(progressCallback, router, callbacks)
      }
    } catch (err) {
      resetLoading(progressCallback)
      trackCollectionError(err)
    } finally {
      resetLoading(progressCallback)
    }
  }

  function parseDateString(dateValue) {
    return (dateValue && new Date(dateValue)) || null
  }

  function parseDeliveryWindowDate(deliveryWindow, field) {
    return deliveryWindow?.[field]
      ? getMonthAndYearFromDate(deliveryWindow[field])
      : null
  }

  // Collections saved before the multi-file change carry single-file fields;
  // fold them into the arrays so the edit form shows one consistent list.
  function normalizeBrandCatalogFiles(data) {
    const fold = (files, url, name) => [
      ...(files || []),
      ...(url ? [{ url, name: name || 'Brand catalogue file' }] : []),
    ]
    return {
      brandCatalogFiles: fold(data.brandCatalogFiles, data.brandCatalogFile, data.brandCatalogFileName),
      brandCatalogAddonFiles: fold(data.brandCatalogAddonFiles, data.brandCatalogAddonFile, data.brandCatalogAddonFileName),
    }
  }

  function applyCollectionData(data) {
    collection.value = { ...collection.value, ...data, ...normalizeBrandCatalogFiles(data) }
    collection.value.endDateString = parseDateString(collection.value.endDate)
    collection.value.startDateString = parseDateString(collection.value.startDate)
    const deliveryWindow = collection.value.expectedDeliveryWindow
    fromDate.value = parseDeliveryWindowDate(deliveryWindow, 'fromDate')
    toDate.value = parseDeliveryWindowDate(deliveryWindow, 'toDate')
    if (collection.value.logo) logoPreview.value = collection.value.logo
  }

  function fetchCollection(updateBreadcrumbCallback) {
    store
      .dispatch(GET_COLLECTION_BY_ID, collectionId.value)
      .then(res => {
        applyCollectionData(res.data.data)
        if (updateBreadcrumbCallback) updateBreadcrumbCallback()
      })
  }

  // KP-3919: brand catalogue files — uploaded immediately on selection,
  // persisted with the collection on Save. `brandCatalogUpload` names the
  // section being uploaded into (plus the current file and progress) so the
  // edit page can show the loader exactly where the files will appear;
  // `brandCatalogUploading` stays the coarse in-flight flag the save guard
  // uses.
  const brandCatalogUploading = ref(false)
  const brandCatalogUpload = ref({
    section: null, fileName: '', done: 0, total: 0, percent: 0, paused: false,
  })
  let brandCatalogControl = null

  function validCollectionAsset(file, allowed) {
    const extension = file.name.split('.').pop().toLowerCase()
    if (!allowed.includes(extension)) {
      apiToastWarning(
        `There is no support for this file format. Choose from ${allowed
          .map(type => `.${type}`)
          .join(', ')}`,
      )
      return false
    }
    return true
  }

  // Which of the picked files may proceed: valid types, and only if the
  // section has room. Returns [] (after explaining why) when it cannot.
  function acceptableAssets(fileList, { allowed, listKey }) {
    const files = Array.from(fileList || []).filter(file => validCollectionAsset(file, allowed))
    if (!files.length) return []
    const existing = (collection.value[listKey] || []).length
    if (existing + files.length > UPLOAD_BRAND_CATALOG_MAX_FILES) {
      apiToastWarning(
        `This section holds up to ${UPLOAD_BRAND_CATALOG_MAX_FILES} files (${existing} uploaded). Remove some files or upload fewer.`,
      )
      return []
    }
    return files
  }

  // One file, start to finish. A resumed row that fails may be poisoned (its
  // S3 multipart can already be gone), so abandon it and let a retry start
  // clean rather than looping on it.
  async function uploadOneAsset(file, fileType) {
    const resumeFrom = await findResumableUpload(file, fileType).catch(() => null)
    try {
      return await uploadMultipart(file, {
        fileType,
        resumeFrom,
        control: brandCatalogControl,
        onProgress: ({ percent }) => {
          brandCatalogUpload.value = { ...brandCatalogUpload.value, percent }
        },
      })
    } catch (err) {
      if (resumeFrom && !(err instanceof UploadCancelled)) {
        abandonUpload(resumeFrom.uploadRecordId).catch(() => {})
      }
      throw err
    }
  }

  // Both sections take MULTIPLE files; each valid one is uploaded on
  // selection and appended to its section's list, persisted on Save.
  async function uploadCollectionAssets(fileList, {
    allowed, listKey, fileType, section,
  }) {
    if (brandCatalogUploading.value) {
      apiToastWarning('Please wait for the current upload to finish.')
      return
    }
    const files = acceptableAssets(fileList, { allowed, listKey })
    if (!files.length) return

    brandCatalogUploading.value = true
    brandCatalogControl = createUploadControl()
    brandCatalogUpload.value = {
      section, fileName: files[0].name, done: 0, total: files.length, percent: 0, paused: false,
    }
    try {
      // Sequential per file (parts within a file upload in parallel):
      // predictable order, and pause/cancel act on one clear target.
      for (const [index, file] of files.entries()) {
        brandCatalogUpload.value = {
          ...brandCatalogUpload.value, section, fileName: file.name, done: index, percent: 0,
        }
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadOneAsset(file, fileType)
        collection.value[listKey] = [
          ...(collection.value[listKey] || []),
          { url: uploaded.url, name: uploaded.name, size: file.size },
        ]
      }
    } catch (err) {
      if (err instanceof UploadCancelled) {
        apiToastWarning('Upload cancelled.')
      } else {
        apiToastFailure(err, 'Could not upload the file. Please try again.')
      }
    } finally {
      brandCatalogControl = null
      brandCatalogUploading.value = false
      brandCatalogUpload.value = {
        section: null, fileName: '', done: 0, total: 0, percent: 0, paused: false,
      }
    }
    await persistBrandCatalogFiles()
  }

  // pause()/cancel() abort the in-flight part PUTs instantly — the click
  // must take effect immediately, not after the current parts drain.
  function pauseBrandCatalogUpload() {
    if (brandCatalogControl) {
      brandCatalogControl.pause()
      brandCatalogUpload.value = { ...brandCatalogUpload.value, paused: true }
    }
  }

  function resumeBrandCatalogUpload() {
    if (brandCatalogControl) {
      brandCatalogControl.resume()
      brandCatalogUpload.value = { ...brandCatalogUpload.value, paused: false }
    }
  }

  function cancelBrandCatalogUpload() {
    if (brandCatalogControl) {
      brandCatalogControl.paused = false
      brandCatalogControl.cancel()
    }
  }

  function uploadBrandCatalogFiles(fileList) {
    return uploadCollectionAssets(fileList, {
      allowed: UPLOAD_BRAND_CATALOG_FILE_TYPES,
      listKey: 'brandCatalogFiles',
      fileType: BRAND_CATALOG_PDF_FILE_TYPE,
      section: 'catalog',
    })
  }

  function uploadBrandCatalogAddonFiles(fileList) {
    return uploadCollectionAssets(fileList, {
      allowed: UPLOAD_BRAND_CATALOG_ADDON_FILE_TYPES,
      listKey: 'brandCatalogAddonFiles',
      fileType: BRAND_CATALOG_ADDON_FILE_TYPE,
      section: 'addon',
    })
  }

  // The file is already in S3 the moment the upload finishes — waiting for
  // Save Changes to persist the list lost uploads on reload (seen live).
  // Persist ONLY the file arrays right away; the rest of the form still
  // belongs to Save Changes. No-op while creating (no id yet — the create
  // call carries the arrays).
  async function persistBrandCatalogFiles() {
    if (!collectionId.value) return
    try {
      await store.dispatch(UPDATE_COLLECTION, {
        collectionId: collectionId.value,
        payload: {
          name: collection.value.name,
          brandCatalogFiles: (collection.value.brandCatalogFiles || []).map(file => ({
            url: file.url, name: file.name, ...(file.size ? { size: file.size } : {}),
          })),
          brandCatalogAddonFiles: (collection.value.brandCatalogAddonFiles || []).map(file => ({
            url: file.url, name: file.name, ...(file.size ? { size: file.size } : {}),
          })),
          brandCatalogFile: '',
          brandCatalogFileName: '',
          brandCatalogAddonFile: '',
          brandCatalogAddonFileName: '',
        },
        loaderObj: {},
      })
    } catch (err) {
      apiToastFailure(err, 'Could not save the uploaded files. Please press Save Changes.')
    }
  }

  function removeBrandCatalogFile(index) {
    collection.value.brandCatalogFiles = collection.value.brandCatalogFiles.filter(
      (_, i) => i !== index,
    )
    persistBrandCatalogFiles()
  }

  function removeBrandCatalogAddonFile(index) {
    collection.value.brandCatalogAddonFiles = collection.value.brandCatalogAddonFiles.filter(
      (_, i) => i !== index,
    )
    persistBrandCatalogFiles()
  }

  function handleFileUpload(file) {
    productFile.value = file
    isFileUpload.value = true
    errors.value.productsFile = null
  }

  function clearFile() {
    productFile.value = null
    isFileUpload.value = false
  }

  function onDropCollectionLogo(e) {
    const droppedFile = e.dataTransfer.files[0]
    const isSupportedFile = UPLOAD_COLLECTION_LOGO_FILE_TYPES.find(type => droppedFile.type.includes(type))
    if (!isSupportedFile) {
      apiToastError(
        'There is no support for this file format. Choose from .jpg, .png, .jpeg, or .svg',
      )
      return null
    }
    img.value = droppedFile
  }

  function onDropProductFile(e) {
    const droppedFile = e.dataTransfer.files[0]
    const isSupportedFile = UPLOAD_CATALOG_FILE_TYPES.find(type => droppedFile.type.includes(type))
    if (!isSupportedFile) {
      apiToastError(
        'There is no support for this file format. Choose either .xlsx or .zip',
      )
      return null
    }
    productFile.value = droppedFile
  }

  function onDownloadSampleSheet(openDownloadModal) {
    if (collectionId.value) {
      if (openDownloadModal) openDownloadModal(collectionId.value)
    } else {
      download(SampleSheet)
    }
  }

  async function cropImage(cropperRef, onDone) {
    isCropping.value = true
    const fileName = img.value?.name || 'collection-logo.png'
    const { canvas } = cropperRef.getResult()
    const logoData = canvas.toDataURL()
    let file = dataURLtoFile(logoData, fileName)
    if (file.size / 1024 > 1024) {
      const compress = new Compress()
      const data = await compress.compress([file], IMAGE_COMPRESSER)
      file = dataURLtoFile(data[0].prefix + data[0].data, fileName)
    }
    logoFile.value = file
    img.value = null
    croppedImg.value = null
    isCropping.value = false
    if (onDone) onDone()
  }

  function getDromo() {
    const license = Settings.getConfig('VUE_APP_DROMO_LICENSE')
    const filesInZipList = ['image1.jpeg', 'image2.jpeg']
    const DromoConstructor = isDromoRefactorEnabled.value ? DromoUploadCatalogHelperV2 : DromoUploadCatalogHelperV1
    const dromoHelper = new DromoConstructor(null, filesInZipList)
    const fields = dromoHelper.fields()
    const settings = dromoHelper.settings()
    const p = store.getters.profile
    const user = {
      id: p?.id,
      name: `${p?.firstName} ${p?.lastName}`,
      email: p?.email,
      companyId: p?.entityId || '',
      companyName: p?.entityName,
    }
    const dromo = new DromoUploader(license, fields, settings, user)
    dromoHelper.registerHooks(dromo)
    return dromo
  }

  function startDromo() {
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_CLICKS_UPLOAD_COLLECTION_USING_DROMO_XLSX,
      formatObject({ brandId: profile.value?.entityId, userId: profile.value?.id }),
    )
    setTimeout(() => {
      isDromoUpload.value = true
    }, 1000)
    productFile.value = null
    isFileUpload.value = false
    const dromo = getDromo()
    dromo.onResults((response, metadata) => {
      dromoId.value = metadata.id
    })
    dromo.beforeFinish(() => {
      isDromoUploadDone.value = true
    })
    dromo.onCancel(() => {
      isDromoUpload.value = false
      isDromoUploadDone.value = false
      isFileUpload.value = false
    })
    dromo.open()
  }

  // Watchers
  watch(logoFile, async theFile => {
    if (!theFile) return
    logoPreview.value = await toBase64(theFile)
  })

  watch(img, async theFile => {
    if (!theFile) {
      croppedImg.value = null
      return
    }
    croppedImg.value = await toBase64(theFile)
  })

  return {
    // Form state
    collectionId,
    collection,
    logoFile,
    logoPreview,
    productFile,
    dromoId,
    isDromoUpload,
    isFileUpload,
    isDromoUploadDone,
    fromDate,
    toDate,

    // Loading state
    uploading,
    loading,
    uploadPercentage,
    uploadMessage,
    uploadTime,
    ordersToUpdate,
    payload,
    isCropping,
    img,
    croppedImg,

    // Computed
    profile,
    isBrand,
    isEdit,
    buttonText,
    title,
    isDromoRefactorEnabled,
    disabled,
    hasDates,

    // Validation
    errors,
    validateField,
    validateAll,

    // Methods
    buildDefault,
    uploadTimeHandler,
    resetForm,
    trackCollectionError,
    uploadLogo,
    uploadProduct,
    resetLoading,
    buildPayload,
    doNotUpdateOrders,
    updateOrdersPayload,
    createCollection,
    updateCollection,
    syncCollection,
    fetchCollection,
    brandCatalogUploading,
    brandCatalogUpload,
    pauseBrandCatalogUpload,
    resumeBrandCatalogUpload,
    cancelBrandCatalogUpload,
    uploadBrandCatalogFiles,
    uploadBrandCatalogAddonFiles,
    removeBrandCatalogFile,
    removeBrandCatalogAddonFile,
    handleFileUpload,
    clearFile,
    onDropCollectionLogo,
    onDropProductFile,
    onDownloadSampleSheet,
    cropImage,
    getDromo,
    startDromo,

    // Constants
    COLLECTION_TYPE,
    KP_DATE_FORMAT,
    KP_MONTH_FORMAT,
    enableDromoDemo,
    formatNumber,
    textTruncate,
    download,
    formattedDate,
  }
}
